import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck } from 'lucide-react'
import { subscribePending, resolvePending, type StepupApp } from '../auth/stepupStore'
import { stepupView, stepupErrorKey, type MethodsSnapshot } from '../auth/stepupView'
import {
  fetchMfaMethods, sendStepupEmailOtp, verifyStepup, verifyEmailOtpEnrollment, regenerateRecoveryCodes,
} from '../lib/pfhApi'
import styles from './StepUpSheet.module.css'

type Method = 'totp' | 'email_otp' | 'recovery_code'
type Phase = 'intro' | 'code' | 'recovery'
const BRAND: Record<StepupApp, string> = { pazuru: 'Naforo', tsumi: 'Yiriba' }
const RESEND_SECONDS = 30

export function StepUpSheet() {
  const { t } = useTranslation()
  const [app, setApp] = useState<StepupApp | null>(null)
  // undefined = still loading, null = the methods fetch failed
  const [snapshot, setSnapshot] = useState<MethodsSnapshot | null | undefined>(undefined)
  const [methods, setMethods] = useState<Method[]>(['totp'])
  const [method, setMethod] = useState<Method>('totp')
  const [phase, setPhase] = useState<Phase>('intro')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const autoSent = useRef(false)

  useEffect(() => subscribePending(setApp), [])

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  useEffect(() => {
    if (!app) return
    setCode(''); setError(null); setSnapshot(undefined); setPhase('intro'); setRecoveryCodes([])
    setSent(false); setCooldown(0); autoSent.current = false
    fetchMfaMethods().then((m) => {
      const list: Method[] = []
      if (m.totp_enabled) list.push('totp')
      if (m.email_otp_enabled) list.push('email_otp')
      if (m.recovery_codes_remaining > 0) list.push('recovery_code')
      const usable = list.length ? list : (['totp'] as Method[])
      setMethods(usable); setMethod(usable[0]); setSnapshot(m)
    }).catch(() => { setMethods(['totp']); setMethod('totp'); setSnapshot(null) })
  }, [app])

  const view = snapshot === undefined ? 'loading' : stepupView(snapshot)

  async function sendCode(): Promise<boolean> {
    setError(null)
    try {
      await sendStepupEmailOtp()
      setSent(true); setCooldown(RESEND_SECONDS)
      return true
    } catch (e) {
      setError(t(`mobile.stepup.errors.${stepupErrorKey(e)}`))
      return false
    }
  }

  // Email-code users get the code without having to find a "send" button first.
  useEffect(() => {
    if (!app || view !== 'verify' || phase === 'recovery' || method !== 'email_otp' || autoSent.current) return
    autoSent.current = true
    void sendCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, view, phase, method])

  if (!app) return null

  async function startEnroll() {
    setBusy(true)
    if (await sendCode()) { setPhase('code'); setCode('') }
    setBusy(false)
  }

  async function confirmEnroll() {
    setBusy(true); setError(null)
    try {
      await verifyEmailOtpEnrollment(code.trim())
    } catch (e) {
      setError(t(`mobile.stepup.errors.${stepupErrorKey(e)}`)); setBusy(false); return
    }
    // Enrolled. Show the recovery codes once; if issuing them fails they can be regenerated in Security.
    const next: MethodsSnapshot = { totp_enabled: false, email_otp_enabled: true, recovery_codes_remaining: 0 }
    try {
      const codes = await regenerateRecoveryCodes()
      setRecoveryCodes(codes); next.recovery_codes_remaining = codes.length; setPhase('recovery')
    } catch {
      setPhase('intro')
    }
    setSnapshot(next); setMethods(['email_otp']); setMethod('email_otp'); setCode('')
    autoSent.current = false; setSent(false); setCooldown(0)
    setBusy(false)
  }

  function finishRecovery() {
    setPhase('intro')
    setMethods(recoveryCodes.length ? ['email_otp', 'recovery_code'] : ['email_otp'])
    setRecoveryCodes([])
  }

  async function verify() {
    if (!app) return
    setBusy(true); setError(null)
    try {
      await verifyStepup(app, method, code.trim())
      resolvePending(true)
    } catch (e) {
      setError(t(`mobile.stepup.errors.${stepupErrorKey(e)}`))
    } finally {
      setBusy(false)
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (view === 'enroll' && phase === 'intro') void startEnroll()
    else if (view === 'enroll' && phase === 'code') void confirmEnroll()
    else if (phase === 'recovery') finishRecovery()
    else if (view === 'verify') void verify()
  }

  const brand = BRAND[app]
  const showRecovery = phase === 'recovery'
  const enrolling = !showRecovery && view === 'enroll'
  const title = showRecovery ? t('mobile.stepup.recovery.title')
    : enrolling ? t('mobile.stepup.enroll.title') : t('mobile.stepup.title')
  const body = showRecovery ? t('mobile.stepup.recovery.body')
    : enrolling ? (phase === 'code' ? t('mobile.stepup.enroll.codeBody') : t('mobile.stepup.enroll.body', { app: brand }))
    : t('mobile.stepup.body', { app: brand })
  const codeReady = code.trim().length >= 6
  const resend = (
    <button type="button" className={styles.link} disabled={busy || cooldown > 0} onClick={() => void sendCode()}>
      {cooldown > 0 ? t('mobile.stepup.resendIn', { s: cooldown }) : t('mobile.stepup.resend')}
    </button>
  )

  return (
    <div className={styles.backdrop} role="presentation">
      <form className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="stepup-title" onSubmit={submit}>
        <ShieldCheck size={28} aria-hidden className={styles.icon} />
        <h2 id="stepup-title" className={styles.title}>{title}</h2>
        <p className={styles.body}>{view === 'loading' ? t('mobile.stepup.loading') : body}</p>

        {showRecovery && (
          <ul className={styles.codes} aria-label={t('mobile.stepup.recovery.title')}>
            {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
          </ul>
        )}

        {!showRecovery && view === 'verify' && (
          <>
            {methods.length > 1 && (
              <div role="radiogroup" className={styles.methods}>
                {methods.map((m) => (
                  <button key={m} type="button" role="radio" aria-checked={method === m}
                    className={method === m ? styles.methodOn : styles.method}
                    onClick={() => { setMethod(m); setCode(''); setError(null) }}>
                    {t(`mobile.stepup.methods.${m}`)}
                  </button>
                ))}
              </div>
            )}
            <p className={styles.hint}>{t(`mobile.stepup.hint.${method}`)}</p>
            {method === 'email_otp' && sent && <p role="status" className={styles.note}>{t('mobile.stepup.sent')}</p>}
            <label className={styles.label}>
              {t('mobile.stepup.code')}
              <input className={styles.input} autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                inputMode={method === 'recovery_code' ? 'text' : 'numeric'} autoComplete="one-time-code" />
            </label>
          </>
        )}

        {enrolling && phase === 'code' && (
          <>
            {sent && <p role="status" className={styles.note}>{t('mobile.stepup.sent')}</p>}
            <label className={styles.label}>
              {t('mobile.stepup.code')}
              <input className={styles.input} autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                inputMode="numeric" autoComplete="one-time-code" />
            </label>
          </>
        )}

        {error && <p role="alert" className={styles.error}>{error}</p>}

        {view !== 'loading' && (
          <button type="submit" className={styles.primary}
            disabled={busy || (showRecovery ? false : (view === 'verify' || phase === 'code') && !codeReady)}>
            {showRecovery ? t('mobile.stepup.recovery.saved')
              : enrolling && phase === 'intro' ? (busy ? t('mobile.stepup.enroll.sending') : t('mobile.stepup.enroll.send'))
              : enrolling ? (busy ? t('mobile.stepup.enroll.confirming') : t('mobile.stepup.enroll.confirm'))
              : busy ? t('mobile.stepup.verifying') : t('mobile.stepup.verify')}
          </button>
        )}
        {(enrolling && phase === 'code') || (!showRecovery && view === 'verify' && method === 'email_otp') ? resend : null}
        {!showRecovery && (
          <button type="button" className={styles.link} onClick={() => resolvePending(false)}>
            {t('mobile.stepup.cancel')}
          </button>
        )}
      </form>
    </div>
  )
}
